import { Link } from "@tanstack/react-router";
import {
  FEEDBACK_TAB_LABELS,
  type FeedbackTab,
} from "./feedbackTabs";

/**
 * Player/guardian feedback sub-tab strip — the nav across the
 * `/feedback` viewer surfaces.
 *
 * PR 1 of the Feedback Player UX redesign: action-first tab set
 * (Overview · Review · Goals · History) replaces the legacy
 * object-type tabs. Overview is the default — bare `/feedback`
 * renders the Overview surface, NOT the legacy Playlists list.
 *
 * Legacy `?tab=` values (`playlists`, `notes`, `clips`, `summaries`,
 * `development`) are still accepted by the router schema and folded
 * to the right action tab via `normalizeFeedbackTab` in the shell.
 * This strip only ever renders the four canonical tabs; the alias is
 * a routing-layer concern, not a UI-layer one.
 */

const TABS: ReadonlyArray<{ id: FeedbackTab; label: string }> = [
  { id: "overview", label: FEEDBACK_TAB_LABELS.overview },
  { id: "review", label: FEEDBACK_TAB_LABELS.review },
  { id: "goals", label: FEEDBACK_TAB_LABELS.goals },
  { id: "history", label: FEEDBACK_TAB_LABELS.history },
];

export type FeedbackTabStripProps = {
  /** The active action tab — the shell computes it from `?tab=` via
   * `normalizeFeedbackTab`. */
  active: FeedbackTab;
};

export function FeedbackTabStrip({ active }: FeedbackTabStripProps) {
  return (
    <nav
      className="flex gap-1 overflow-x-auto rounded border border-rule bg-surface p-1 shadow-sm"
      role="tablist"
      aria-label="My feedback"
      data-testid="feedback-tab-strip"
    >
      {TABS.map((tab) => {
        const isActive = tab.id === active;
        // Overview is the default — no `?tab=` param. Keeps bare
        // `/feedback` as the canonical action-first URL.
        const linkProps =
          tab.id === "overview"
            ? ({ to: "/feedback", search: {} } as const)
            : ({ to: "/feedback", search: { tab: tab.id } } as const);
        return (
          <Link
            key={tab.id}
            {...linkProps}
            role="tab"
            aria-selected={isActive}
            data-feedback-tab={tab.id}
            data-active={isActive}
            className={[
              "shrink-0 rounded px-3 py-1.5 text-sm font-display font-semibold uppercase tracking-wide transition-colors",
              isActive
                ? "bg-accent text-accent-ink"
                : "text-ink-muted hover:bg-field hover:text-ink",
            ].join(" ")}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

// Re-export so existing imports of `FeedbackTab` from this module
// (legacy callers) continue to type-check. The canonical home for the
// type is `feedbackTabs.ts`.
export type { FeedbackTab };
